import { CacheService } from '../Services/CacheService';
import { REDIRECT_STORAGE_KEY } from './redirectAfterLogin';
import { MCP_CONTINUATION_KEY, pendingMcpLogin } from '../features/mcp/continuation';

/**
 * Emergency reset utility to clear all client-side state and cache
 * This can be called remotely or triggered by a version mismatch
 */
export class EmergencyReset {
  private static readonly RESET_VERSION_KEY = 'appResetVersion';
  private static readonly CURRENT_VERSION = '1.0.1'; // Increment this to force reset

  /**
   * Check if a reset is needed and perform it
   */
  static async checkAndReset(): Promise<void> {
    try {
      const currentVersion = localStorage.getItem(this.RESET_VERSION_KEY);
      
      if (currentVersion !== this.CURRENT_VERSION) {
        console.log('Emergency reset triggered - clearing all client state');
        await this.performReset();
        localStorage.setItem(this.RESET_VERSION_KEY, this.CURRENT_VERSION);
        console.log('Emergency reset completed');
      }
    } catch (error) {
      console.error('Error during emergency reset:', error);
    }
  }

  /**
   * Force a reset regardless of version
   */
  static async forceReset(): Promise<void> {
    console.log('Force reset triggered');
    await this.performReset();
    localStorage.setItem(this.RESET_VERSION_KEY, this.CURRENT_VERSION);
    console.log('Force reset completed');
  }

  /**
   * Perform the actual reset
   */
  private static async performReset(): Promise<void> {
    try {
      // Clear all cache
      await CacheService.deleteDatabase();
      
      // Clear localStorage (except for critical items)
      const criticalItems = ['user', 'cacheVersion', 'appResetVersion', REDIRECT_STORAGE_KEY];
      const keysToKeep = new Set(criticalItems);
      
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && !keysToKeep.has(key)) {
          localStorage.removeItem(key);
        }
      }
      
      // Clear sessionStorage
      const mcpContinuation = pendingMcpLogin() ? sessionStorage.getItem(MCP_CONTINUATION_KEY) : null;
      sessionStorage.clear();
      if (mcpContinuation) sessionStorage.setItem(MCP_CONTINUATION_KEY, mcpContinuation);
      
      // Clear any IndexedDB databases
      const databases = await window.indexedDB.databases();
      for (const db of databases) {
        if (db.name && db.name !== 'intenticoCache') {
          window.indexedDB.deleteDatabase(db.name);
        }
      }
      
      // Force page reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error during reset:', error);
      // Fallback: just reload the page
      window.location.reload();
    }
  }

  /**
   * Get current reset version
   */
  static getCurrentVersion(): string {
    return this.CURRENT_VERSION;
  }

  /**
   * Check if reset is needed without performing it
   */
  static isResetNeeded(): boolean {
    const currentVersion = localStorage.getItem(this.RESET_VERSION_KEY);
    return currentVersion !== this.CURRENT_VERSION;
  }
}
